import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import { Button, Flex, Typography } from 'antd';

import axios from '../../../axios';

import { hasPermission } from '../../../helpers/user';

import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { useModal } from '../../../hooks/useModal';

import { fetchAnimals } from '../../../redux/actions/animal';
import { fetchFarms } from '../../../redux/actions/farm';
import { fetchTag } from '../../../redux/actions/tag';

import LoadingBar from '../../../components/LoadingBar';
import PrevNextButtons from '../../../components/PrevNextButtons';
import { errorToastHandler } from '../../../components/action_notifier';
import Livestock from '../../../components/animals/Livestock';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import TagDetails from './TagDetails';
import TagHistory from './TagHistory';

const AppModal = lazy(() => import('../../../components/AppModal'));
const TagEditForm = lazy(() => import('./TagEditForm'));
const UnlinkedLivestockTable = lazy(() => import('./UnlinkedLivestockTable'));

const { Title } = Typography;

function Tag({ history }) {
  const { id } = useParams();

  const dispatch = useDispatch();

  const [archiveModalVisible, openArchiveModal, closeArchiveModal] = useModal();
  const [state, setState] = useState({
    edit_modal: false,
    link_modal: false,
    linking: false,
    re_linking: false,
    selected_animals: [],
    tag_edit: {},
    unlink_modal: false
  });

  const { tag, loading } = useSelector(state => state.tag);
  const { permissions: userPermissions } = useSelector(state => state.user);
  const { options } = useSelector(state => state.farm);
  const { data: animals } = useSelector(state => state.animal);

  useEffect(() => {
    getTag();
    getAnimals();
  }, []);

  // useEffect(() => {
  //   getFarms();
  // }, [id]);

  // useEffect(() => {
  //   setState(prevState => ({ ...prevState, tag_edit: tag }));
  // }, [tag]);

  const actions = useMemo(() => {
    return [
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={
              !tag?.animal ||
              state.re_linking ||
              !hasPermission([PERMISSIONS.TAG_ANIMAL_LINK.UPDATE_OWN], userPermissions)
            }
            onClick={() => toggleModal('unlink_modal')}>
            Unlink tag
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={
              tag?.animal !== null ||
              state.linking ||
              !hasPermission([PERMISSIONS.TAG_ANIMAL_LINK.CREATE], userPermissions)
            }
            onClick={() => setState(prevState => ({ ...prevState, linking: !prevState.linking }))}>
            Link tag
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!(state.linking || state.re_linking)}
            onClick={() => setState(prevState => ({ ...prevState, re_linking: false, linking: false }))}>
            Cancel linking
          </Button>
        )
      },
      {
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!hasPermission([PERMISSIONS.TAG_ANIMAL_LINK.DELETE_OWN], userPermissions)}
            onClick={openArchiveModal}>
            Archive tag
          </Button>
        )
      }
      // {
      //   label: (
      //     <Button
      //       color="default"
      //       size="small"
      //       variant="link"
      //       disabled={!hasPermission([PERMISSIONS.TAG_ANIMAL_LINK.DELETE_OWN], userPermissions)}
      //       onClick={() => toggleModal('edit_modal')}>
      //       Edit tag
      //     </Button>
      //   )
      // }
    ];
  }, [state.linking, state.re_linking, tag?.animal, userPermissions]);

  async function getTag() {
    try {
      await dispatch(fetchTag(id)).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function getAnimals(query = '') {
    try {
      await dispatch(fetchAnimals({ query })).unwrap();
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    }
  }

  async function deleteTag() {
    const response = await axios.delete('tags/' + tag?.id);

    if (response.status === 200) history.push('tags');
  }

  async function unlinkTag() {
    await axios.delete('links', { data: { tag_id: tag?.id } });
    await getTag();

    toggleModal('unlink_modal');

    setState(prevState => ({ ...prevState, selected_animals: [], linking: false }));
  }

  async function linkTag() {
    const animalId = state.selected_animals?.at(0);
    const animal = animals?.find(a => a.id === animalId);

    state.re_linking
      ? await axios.put('links', { tag_id: tag?.id, animal_id: animal.id })
      : await axios.post('links', { tag_id: tag?.id, animal_id: animal.id });

    toggleModal('link_modal');
    await getTag();
    setState(prevState => ({ ...prevState, re_linking: false, linking: !state.linking }));
  }

  function toggleModal(modal, id) {
    setState(prevState => ({ ...prevState, [modal]: !prevState[modal], selected_animals: id ? [id] : [] }));
  }

  async function saveTag() {
    const response = await axios.put('tags/' + id, {
      ...state.tag_edit
    });

    if (response.status === 200) {
      getTag();
      toggleModal('edit_modal');
    }
  }

  async function getFarms() {
    await dispatch(fetchFarms());
  }

  const breadcrumbItems = [
    {
      title: <Link to="/devices">List of Kraal Tags</Link>
    },
    {
      title: `Tag ${tag?.diagri_id}`
    }
  ];

  return (
    <>
      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>Tag {tag?.diagri_id}</Title>

              <AppBreadcrumb items={breadcrumbItems} />
            </Flex>
            <Flex gap="small">
              {actions?.length > 0 && <AppDropdown label="Actions" items={actions} />}

              <PrevNextButtons
                nextId={tag?.next_id}
                prevId={tag?.prev_id}
                handlePrev={() => history.push(`/tag/${tag?.prev_id}`)}
                handleNext={() => history.push(`/tag/${tag?.next_id}`)}
              />
            </Flex>
          </Flex>

          <Flex vertical={true}>
            <TagDetails tag={tag} />

            {(state.linking || state.re_linking) && (
              <Suspense fallback={<LoadingBar />}>
                <UnlinkedLivestockTable
                  data={animals?.filter(a => a.tag === null)}
                  handleLinkLivestock={id => toggleModal('link_modal', id)}
                  handleSearch={getAnimals}
                />
              </Suspense>
            )}

            <TagHistory tagId={id} />
          </Flex>

          <Suspense>
            <AppModal
              isOpen={state.edit_modal}
              confirmButtonText="Update Tag"
              title="Add tag"
              handleCancel={() => toggleModal('edit_modal')}
              handleConfirm={saveTag}>
              <div className="py-4">
                <TagEditForm options={options} />
              </div>
            </AppModal>

            <AppModal
              isOpen={archiveModalVisible}
              confirmButtonColor="danger"
              confirmButtonText="Archive"
              title="Archive Kraal Tag"
              handleCancel={closeArchiveModal}
              handleConfirm={deleteTag}>
              <div className="py-4">
                Are you sure you want to archive this tag? Livestock must be un-linked before the tag can be archived
                and this action cannot be undone.
              </div>
            </AppModal>

            <AppModal
              isOpen={state.link_modal}
              confirmButtonText="Link"
              title="Linking confirmation"
              handleCancel={() => toggleModal('link_modal')}
              handleConfirm={linkTag}>
              <div className="py-4">
                <p className="text-center font-medium">
                  Tag <b>{tag?.diagri_id}</b> will be linked to
                </p>
                <Livestock animals={animals} animal={state.selected_animals} />
              </div>
            </AppModal>

            {tag?.animal && (
              <AppModal
                isOpen={state.unlink_modal}
                confirmButtonColor="danger"
                confirmButtonText="Unlink"
                title="Unlinking confirmation"
                handleCancel={() => toggleModal('unlink_modal')}
                handleConfirm={unlinkTag}>
                <div className="py-4">
                  <p className="text-center font-medium">Tag will be unlinked with</p>
                  <Livestock animals={animals} animal={[tag?.animal.id]} />
                </div>
              </AppModal>
            )}
          </Suspense>
        </>
      )}
    </>
  );
}

export default Tag;
